import * as XLSX from 'xlsx';
import {
  useEffect,
  useState,
  useRef,
  ChangeEvent,
  forwardRef,
  useImperativeHandle
} from 'react';
import {
  Box,
  Card,
  Grid,
  Button,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  TableCell,
  TextField
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { useDispatch, useSelector } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import { getVarianceReport } from 'src/services/timeManagementService';
import NoRecord from '../common/NoRecord';
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import ClientDropdown from '../common/ClientDropdown';
import ProjectDropdownFilter from '../common/ProjectDropdownFilter';
import { TVarianceReportSummary } from '../../types/time-entry-report.type';
import { IVarianceReportFilter } from '../../interface/time-entry-report.interface';
import { useForm } from 'react-hook-form';
import ResourceDropdownFilter from '../common/ResourceDropdownFilter';

interface VarianceReportTableProps {
  permissions: any;
}

export interface VarianceReportTableRef {
  handleDownload: () => void;
}

const VarianceReportTable = forwardRef<
  VarianceReportTableRef,
  VarianceReportTableProps
>(({ permissions }, ref) => {
  const paginationWrapperRef = useRef(null);
  const dispatch = useDispatch();
  const myRef = useRef<DateRangePicker>(null);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [toggleFilter, setToggleFilter] = useState(false);
  const filterPayload: IVarianceReportFilter = {
    startDate: moment(new Date()).format('YYYY-MM-DD'),
    endDate: moment(new Date()).format('YYYY-MM-DD'),
    employeeIds: [],
    projectIds: [],
    customerIds: []
  };

  const initialFilter: IVarianceReportFilter = {
    ...filterPayload,
    limit: 10,
    offset: 1
  };

  const [filter, setFilter] = useState<IVarianceReportFilter>(initialFilter);

  const { register, handleSubmit, reset, setValue } =
    useForm<IVarianceReportFilter>({
      defaultValues: initialFilter
    });

  const [data, setData] = useState<TVarianceReportSummary[]>([]);
  const [total, setTotal] = useState<number>(0);

  const handlePageChange = (event: any, newPage: number): void => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      offset: newPage + 1
    }));
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      limit: parseInt(event.target.value),
      offset: 1
    }));
  };

  const convertTimeFormat = (time: string): string => {
    const [hours, minutes] = time
      .split('h ')
      .map((part) => part.replace('m', '').trim());
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const fetchData = (filter: IVarianceReportFilter) => {
    dispatch(AppActions.showLoader());
    const filterPayload = {
      startDate: filter.startDate,
      endDate: filter.endDate,
      employeeIds: filter.employeeIds,
      projectIds: filter.projectIds,
      customerIds: filter.customerIds
    };

    getVarianceReport(filterPayload, filter.offset, filter.limit)
      .then((res) => {
        const data = res?.data?.data.employeeHours.map(
          (item: TVarianceReportSummary) => ({
            ...item,
            leaveHours: convertTimeFormat(item.leaveHours),
            expectedHours: item.expectedHours
              ? convertTimeFormat(item.expectedHours)
              : '0h 0m',
            planedHours: item.planedHours
              ? convertTimeFormat(item.planedHours)
              : '0h 0m',
            enteredHours: convertTimeFormat(item.enteredHours)
          })
        );
        setData(data || []);
        setTotal(res?.data?.data.totalCount || 0);
        dispatch(AppActions.hideLoader());
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
      });
  };

  useEffect(() => {
    fetchData(filter);
  }, [filter]);

  const onSubmit = (data: IVarianceReportFilter) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      ...data,
      offset: 1
    }));
  };

  const resetFilterResult = () => {
    reset();
    const today = new Date();
    myRef.current?.setStartDate(today);
    myRef.current?.setEndDate(today);

    setFilter({
      ...initialFilter,
      startDate: moment(today).format('YYYY-MM-DD'),
      endDate: moment(today).format('YYYY-MM-DD')
    });
  };

  const handleDownload = async () => {
    if (data.length === 0) {
      setSnackBarMessage('No data available to download.');
      setSnackbarSeverity('warning');
      setSnackBarOpen(true);
      return;
    }

    const fetchAllData = async () => {
      const allData = [];
      let currentPage = 1;
      const limit = total;

      while (true) {
        const res = await getVarianceReport(filter, currentPage, limit);
        const data = res?.data?.data.employeeHours.map(
          (item: TVarianceReportSummary) => ({
            ...item,
            leaveHours: convertTimeFormat(item.leaveHours),
            expectedHours: item.expectedHours
              ? convertTimeFormat(item.expectedHours)
              : '0h 0m',
            planedHours: item.planedHours
              ? convertTimeFormat(item.planedHours)
              : '0h 0m',
            enteredHours: convertTimeFormat(item.enteredHours)
          })
        );
        allData.push(...data);

        if (allData.length >= res?.data?.data.totalCount) {
          break;
        }
        currentPage++;
      }
      return allData;
    };

    const allData = await fetchAllData();
    const timestamp = moment().format('YYYYMMDD_HHmmss');
    const headers = [
      [
        'Resource Name',
        'Planned Hours',
        'Leave Hours',
        'Expected Hours',
        'Entered Hours'
      ]
    ];
    const ws = XLSX.utils.aoa_to_sheet(headers);
    XLSX.utils.sheet_add_json(ws, allData, { origin: 'A2', skipHeader: true });

    const wsCols = headers[0].map((header, index) => {
      const maxLength = Math.max(
        header.length,
        ...allData.map(
          (row) => (row[Object.keys(row)[index]] || '').toString().length
        )
      );
      return { wch: maxLength + 2 };
    });
    ws['!cols'] = wsCols;

    const range = XLSX.utils.decode_range(ws['!ref']!);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell_address = XLSX.utils.encode_cell({ c: C, r: 0 });
      if (!ws[cell_address]) continue;
      ws[cell_address].s = {
        font: {
          name: 'Times New Roman',
          bold: true,
          sz: 12
        },
        alignment: {
          vertical: 'center',
          horizontal: 'center'
        },
        fill: {
          type: 'pattern',
          patternType: 'solid',
          fgColor: { rgb: '3498DB' }
        },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };
    }

    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = XLSX.utils.encode_cell({ c: C, r: R });
        if (!ws[cell_address]) continue;
        ws[cell_address].s = {
          font: {
            name: 'Times New Roman',
            sz: 11
          },
          alignment: {
            vertical: 'center',
            horizontal: 'center'
          },
          border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } }
          }
        };
      }
    }

    ws['!autofilter'] = { ref: ws['!ref'] };
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'VarianceReport');
    XLSX.writeFile(wb, `VarianceReport_${timestamp}.xlsx`);
  };

  useImperativeHandle(ref, () => ({
    handleDownload
  }));

  const getInitSettings = () => {
    const today = new Date();
    let settings = {
      startDate: today,
      endDate: today
    };
    return settings;
  };

  return (
    <div className="variance-report-table">
      <Card>
        <Grid
          sx={{ px: 2, py: 2 }}
          item
          xs={12}
          display="flex"
          justifyContent="space-between"
          spacing={1}
        >
          <Button
            id="btn-setToggleFilter"
            sx={{
              ml: 1,
              mt: { xs: 2, md: 0 },
              color: `${!toggleFilter && 'gray'}`
            }}
            variant="outlined"
            startIcon={<FilterListIcon style={{ fontSize: '15px' }} />}
            onClick={() => {
              setToggleFilter(!toggleFilter);
            }}
            style={{ height: '1.75rem', fontSize: '12px' }}
          >
            Filters
          </Button>
          <Grid style={{ fontSize: '12px' }}>
            Data display is for the duration :{' '}
            {moment(filter?.startDate).format('YYYY MMM DD') +
              ' - ' +
              moment(filter?.endDate).format('YYYY MMM DD')}
          </Grid>
        </Grid>
        {toggleFilter && (
          <Grid px={2} pb={2} style={{ margin: 0 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                spacing={1}
                style={{
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap',
                  alignItems: 'center'
                }}
              >
                <Grid item xs={3} width="20%">
                  <ClientDropdown
                    title="Client"
                    {...register('customerIds')}
                    value={filter?.customerIds.join(',') || null}
                    onChange={(selected: string) => {
                      const selectedArray = selected.split(',').filter(Boolean);
                      setFilter((prev) => ({
                        ...prev,
                        customerIds: selectedArray,
                        offset: 1
                      }));
                      setValue('customerIds', selectedArray);
                    }}
                  />
                </Grid>
                <Grid item xs={3} width="20%">
                  <ProjectDropdownFilter
                    title="Project"
                    {...register('projectIds')}
                    value={filter?.projectIds?.join(',') || null}
                    projectId={filter?.projectIds?.join(',') || ''}
                    customerIds={filter?.customerIds || []}
                    onChange={(selected: string) => {
                      const selectedArray = selected.split(',').filter(Boolean);
                      setFilter((prev) => ({
                        ...prev,
                        projectIds: selectedArray,
                        offset: 1
                      }));
                      setValue('projectIds', selectedArray);
                    }}
                  />
                </Grid>
                <Grid item xs={3} width="20%">
                  <ResourceDropdownFilter
                    title="Resource Name"
                    {...register('employeeIds')}
                    value={filter?.employeeIds?.join(',') || ''}
                    projectIds={filter?.projectIds || []}
                    customerIds={filter?.customerIds || []}
                    startDate={
                      filter?.startDate
                        ? moment(filter.startDate).format('YYYY-MM-DD')
                        : null
                    }
                    endDate={
                      filter?.endDate
                        ? moment(filter.endDate).format('YYYY-MM-DD')
                        : null
                    }
                    onChange={(selected: string) => {
                      const selectedArray = selected
                        ? selected.split(',').filter(Boolean)
                        : [];
                      setFilter((prev) => ({
                        ...prev,
                        employeeIds: selectedArray,
                        offset: 1
                      }));
                      setValue('employeeIds', selectedArray);
                    }}
                  />
                </Grid>
                <Grid item xs={3} width="20%">
                  <div className="date-range-picker">
                    <DateRangePicker
                      ref={myRef}
                      initialSettings={getInitSettings()}
                      onApply={(event, picker) => {
                        setFilter((prev) => ({
                          ...prev,
                          startDate: moment(picker.startDate).format(
                            'YYYY-MM-DD'
                          ),
                          endDate: moment(picker.endDate).format('YYYY-MM-DD')
                        }));
                        myRef.current?.setStartDate(picker.startDate);
                        myRef.current?.setEndDate(picker.endDate);

                        setValue(
                          'startDate',
                          moment(picker.startDate).format('YYYY-MM-DD')
                        );
                        setValue(
                          'endDate',
                          moment(picker.endDate).format('YYYY-MM-DD')
                        );
                      }}
                      onCancel={() => {
                        const today = new Date();
                        myRef.current?.setStartDate(today);
                        myRef.current?.setEndDate(today);

                        setFilter((prev) => ({
                          ...prev,
                          startDate: moment(today).format('YYYY-MM-DD'),
                          endDate: moment(today).format('YYYY-MM-DD')
                        }));
                        setValue(
                          'startDate',
                          moment(today).format('YYYY-MM-DD')
                        );
                        setValue('endDate', moment(today).format('YYYY-MM-DD'));
                      }}
                    >
                      <div style={{ position: 'relative' }}>
                        <TextField
                          placeholder="YYYY-MM-DD - YYYY-MM-DD"
                          value={filter?.startDate + ' - ' + filter?.endDate}
                          id="date"
                          fullWidth
                          size="small"
                          style={{ paddingTop: 8, fontSize: '12px' }}
                        />
                      </div>
                    </DateRangePicker>
                  </div>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={1}
                style={{
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  marginTop: '5px'
                }}
              >
                <Grid
                  item
                  xs={3}
                  width="60%"
                  style={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginLeft: 'auto',
                    marginTop: '8px',
                    display: 'flex'
                  }}
                >
                  <Button
                    sx={{ mt: { xs: 2, md: 0 }, mr: 1 }}
                    variant="outlined"
                    onClick={resetFilterResult}
                    style={{ height: '1.75rem', fontSize: '12px' }}
                    id="btn-resetFilterResult"
                  >
                    Clear
                  </Button>
                  <Button
                    sx={{ mt: { xs: 2, md: 0 } }}
                    id="btn-onSubmit"
                    variant="outlined"
                    type="submit"
                    style={{ height: '1.75rem', fontSize: '12px' }}
                  >
                    Filter
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        )}
        <Divider />
        <TableContainer
          className="tableContainer"
          style={{ height: data?.length === 0 || !data ? '300px' : 'auto' }}
        >
          <Table
            style={{ borderCollapse: 'separate', position: 'relative' }}
            size="small"
          >
            <TableHead>
              <TableRow style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableHeaderCell content="Resource Name" smallFont />
                <TableHeaderCell content="Planned Hours" smallFont />
                <TableHeaderCell content="Leave Hours" smallFont />
                <TableHeaderCell content="Expected Hours" smallFont />
                <TableHeaderCell content="Entered Hours" smallFont />
              </TableRow>
            </TableHead>
            <TableBody>
              {data &&
                data?.length > 0 &&
                data.map((report, i) => {
                  return (
                    <TableRow hover key={i}>
                      <TableCell sx={{ py: 2 }}>{report.resource}</TableCell>
                      <TableCell sx={{ py: 2 }}>{report.planedHours}</TableCell>
                      <TableCell sx={{ py: 2 }}>{report.leaveHours}</TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {report.expectedHours}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {report.enteredHours}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          {(data?.length === 0 || !data) && <NoRecord />}
        </TableContainer>
        <Box
          p={1}
          ref={paginationWrapperRef}
          sx={{ borderTop: '1px solid #eee' }}
        >
          <TablePagination
            sx={{
              fontSize: '12px',
              '.MuiTablePagination-displayedRows': {
                fontSize: '12px'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '12px'
              }
            }}
            component="div"
            count={total ? total : 0}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={filter.offset - 1}
            rowsPerPage={filter.limit}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>
      </Card>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </div>
  );
});

export default VarianceReportTable;
